import {
  Component,
  Input,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DelayCausedBy } from 'src/app/services/gantt/gantt.service';
import {
  DelayedServiceService,
  DelayReasonDto,
  UpdateDelayPayload,
} from './service/delayed-service.service';
import { TuiAlertService } from '@taiga-ui/core';
import { DelayType } from '../gantt/interfaces/gantt-details.model';

interface DelayReason {
  code: string;
  labelKey: string;
}

@Component({
  selector: 'app-modal-delayed-service',
  templateUrl: './modal-delayed-service.component.html',
  styleUrls: ['./modal-delayed-service.component.css'],
})
export class ModalDelayedServiceComponent implements OnInit {
  @Input({ required: true }) orderHash!: string;
  @Input({ required: true }) nodeId!: string;
  @Input() existingDelayType?: DelayType;
  @Input() existingReasonGuid?: string;

  form: FormGroup;
  saving = false;
  reasonsLoading = false;
  hasExistingReason = false;

  readonly options: Array<{ value: DelayCausedBy; labelKey: string }> = [
    { value: 'CAFLER', labelKey: 'service_manager.delay.caused_by.cafler' },
    { value: 'CLIENT', labelKey: 'service_manager.delay.caused_by.client' },
  ];

  private readonly reasonsBySource: Partial<
    Record<DelayCausedBy, DelayReason[]>
  > = {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly activeModal: NgbActiveModal,
    private readonly translate: TranslateService,
    private readonly delayedService: DelayedServiceService,
    private readonly alertService: TuiAlertService
  ) {
    this.form = this.fb.group({
      causedBy: ['CLIENT' as DelayCausedBy, Validators.required],
      reasonCode: [null as string | null, Validators.required],
    });

    this.form.get('causedBy')!.valueChanges.subscribe((value) => {
      const causedBy = value as DelayCausedBy;
      this.form.get('reasonCode')!.reset(null);
      this.loadReasons(causedBy);
    });
  }

  ngOnInit(): void {
    this.hasExistingReason = this.existingReasonGuid !== "00000000-0000-0000-0000-000000000000";

    // Precargar valores existentes del backend si hay
    if (this.existingDelayType && this.existingReasonGuid) {
      const causedBy: DelayCausedBy =
        this.existingDelayType === 1 ? 'CLIENT' : 'CAFLER';

      this.form.patchValue({ causedBy }, { emitEvent: false });

      // Cargar motivos y luego buscar el Code correspondiente al Guid
      this.reasonsLoading = true;
      this.delayedService
        .getDelayReasonDetails(causedBy)
        .pipe(
          take(1),
          finalize(() => {
            this.reasonsLoading = false;
          })
        )
        .subscribe({
          next: (reasons: DelayReasonDto[]) => {
            const reason = reasons.find(
              (r) => r.Id === this.existingReasonGuid
            );
            if (reason) {
              this.form.patchValue(
                { reasonCode: reason.Code },
                { emitEvent: false }
              );
            }
          },
          error: (error: unknown) => {
            console.error(
              '[ModalDelayedService] Error loading existing reason',
              error
            );
          },
        });

      this.loadReasons(causedBy);
    } else {
      const initial = this.form.get('causedBy')!.value as DelayCausedBy;
      this.loadReasons(initial);
    }
  }

  get reasons(): DelayReason[] {
    const who = this.form.get('causedBy')!.value as DelayCausedBy;
    return this.reasonsBySource[who] ?? [];
  }

    get headerTitleKey(): string {
    return this.hasExistingReason
      ? 'service_manager.delay.title_edit'
      : 'service_manager.delay.title_add';
  }

  close(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const causedBy = this.form.value.causedBy as DelayCausedBy;
    const reasonCode = this.form.value.reasonCode as string;

    const reasons = this.reasonsBySource[causedBy] ?? [];
    const selectedReason = reasons.find((r) => r.code === reasonCode);

    if (!selectedReason) {
      console.error(
        '[ModalDelayedService] No se encontró el motivo seleccionado'
      );
      return;
    }

    this.loadReasonDetails(causedBy, reasonCode).then((reasonGuid) => {
      if (!reasonGuid) {
        const msg = this.translate.instant('service_manager.delay.save_error');
        this.alertService
          .open(msg, { status: 'error', autoClose: true })
          .subscribe();
        return;
      }

      const delayType = causedBy === 'CLIENT' ? 1 : 2;

      const payload: UpdateDelayPayload = {
        DelayType: delayType,
        DelayReasonGuid: reasonGuid,
      };

      this.saving = true;
      this.delayedService
        .updateServiceDelay(this.orderHash, payload)
        .pipe(
          take(1),
          finalize(() => (this.saving = false))
        )
        .subscribe({
          next: () => {
            this.activeModal.close('saved');
          },
          error: (err: unknown) => {
            console.error('[ModalDelayedService] save error', err);

            const base = this.translate.instant(
              'service_manager.delay.save_error'
            );
            const extra =
              typeof err === 'object' && err && 'message' in err
                ? (err as { message: string }).message
                : '';
            const msg = extra ? `${base}: ${extra}` : base;

            this.alertService
              .open(msg, { status: 'error', autoClose: true })
              .subscribe();
          },
        });
    });
  }

  private loadReasons(causedBy: DelayCausedBy): void {
    if (this.reasonsBySource[causedBy]?.length) {
      return;
    }

    this.reasonsLoading = true;

    this.delayedService
      .getDelayReasons(causedBy)
      .pipe(
        take(1),
        finalize(() => {
          this.reasonsLoading = false;
        })
      )
      .subscribe({
        next: (reasons: DelayReason[]) => {
          this.reasonsBySource[causedBy] = reasons ?? [];
        },
        error: (error: unknown) => {
          console.error(
            '[ModalDelayedService] loadReasons error for',
            causedBy,
            error
          );

          this.reasonsBySource[causedBy] = [];

          const msg = this.translate.instant(
            'service_manager.delay.load_error'
          );

          this.alertService
            .open(msg, { status: 'error', autoClose: true })
            .subscribe();
        },
      });
  }

  private async loadReasonDetails(
    causedBy: DelayCausedBy,
    reasonCode: string
  ): Promise<string | null> {
    return new Promise((resolve) => {
      this.delayedService
        .getDelayReasonDetails(causedBy)
        .pipe(take(1))
        .subscribe({
          next: (reasons: DelayReasonDto[]) => {
            const reason = reasons.find((r) => r.Code === reasonCode);
            resolve(reason?.Id ?? null);
          },
          error: (error: unknown) => {
            console.error(
              '[ModalDelayedService] loadReasonDetails error',
              error
            );
            resolve(null);
          },
        });
    });
  }
}
